import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { McpPendingActionToken } from '@sleed/bizcommerce-api';
import { mcpPendingActionsApi } from '~/lib/api/client';

/**
 * The AI pending-actions approval queue (docs/ai/16): risk-gated tool calls the BizWeb.NC.AI gate
 * QUEUED instead of executing (docs/ai/13). The server enforces the System role and terminal-state
 * single-fire on approve/reject (409 = already handled elsewhere); approving EXECUTES the action.
 * One `getAll(false)` fetch serves both the pending and history views (queues are small by design —
 * they only accumulate Destructive/over-threshold calls).
 */
export const aiPendingActionKeys = {
	all: ['ai-pending-actions'] as const,
	list: () => [...aiPendingActionKeys.all, 'list'] as const
};

export function useAiPendingActionsList() {
	return useQuery<McpPendingActionToken[]>({
		queryKey: aiPendingActionKeys.list(),
		queryFn: async () => (await mcpPendingActionsApi.getAll(false)).data.items ?? []
	});
}

export function useAiPendingActionMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: aiPendingActionKeys.list() });

	/** Executes the queued tool call once; the result rides `{executed, message}` (200 even on tool failure). */
	const approve = useMutation({
		mutationFn: async (id: number) => (await mcpPendingActionsApi.approve(id)).data,
		onSettled: invalidate // a 409 also means the row changed under us — always refresh
	});
	const reject = useMutation({
		mutationFn: async (id: number) => (await mcpPendingActionsApi.reject(id)).data,
		onSettled: invalidate
	});

	return { approve, reject };
}
